import React, { useState, useEffect, useCallback } from 'react'
import { Card, Tabs, Tag, Typography, Statistic, Input, Button, Table, message, Space, Descriptions, Badge } from 'antd'
import {
  CrownOutlined, ClockCircleOutlined, CalendarOutlined, CopyOutlined,
  IdcardOutlined, TeamOutlined, CheckCircleOutlined, CloseCircleOutlined, LockOutlined
} from '@ant-design/icons'
import { backend } from '../../api'
import { FEATURE_DEFS } from '../../checkFeature'

const { Title, Text, Paragraph } = Typography

const LEVEL_COLORS = { basic: 'blue', vip: 'gold', pro: 'geekblue', admin: 'red' }
const LEVEL_NAMES = { basic: '基础版', vip: '会员版', pro: '专业版', admin: '管理员' }
const DEFAULT_PLANS = [
  { level: 'basic', name: '基础版', price: '免费', features: ['account_list', 'basic_monitor', 'single_publish'] },
  { level: 'vip', name: '会员版', price: '99元/月', features: ['account_list', 'basic_monitor', 'advanced_monitor', 'single_publish', 'multi_publish', 'auto_reply', 'interaction', 'data_monitor', 'analytics', 'proxy_support'] },
  { level: 'pro', name: '专业版', price: '299元/月', features: ['account_list', 'basic_monitor', 'advanced_monitor', 'single_publish', 'multi_publish', 'auto_reply', 'interaction', 'data_monitor', 'analytics', 'proxy_support', 'team_collaboration', 'api_access', 'priority_support', 'dedicated_fingerprints'] }
]

function getRemainingDays(expireAt) {
  if (!expireAt) return 0
  const diff = new Date(expireAt).getTime() - Date.now()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

export default function AccountCenter() {
  const [activeTab, setActiveTab] = useState('membership')
  const [plans, setPlans] = useState(DEFAULT_PLANS)
  const [referralInfo, setReferralInfo] = useState(null)
  const [referralCode, setReferralCode] = useState('')
  const [records, setRecords] = useState([])
  const [refLoading, setRefLoading] = useState(false)
  const [renewalCode, setRenewalCode] = useState('')
  const [renewing, setRenewing] = useState(false)
  const [refReward, setRefReward] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)

  const raw = sessionStorage.getItem('auth_user')
  const user = raw ? JSON.parse(raw) : {}
  const isAdmin = user?.role === 'admin'
  const level = user?.membership_level || user?.level || 'basic'
  const expireAt = user?.membership_expire || user?.expire_at || user?.expires_at || user?.expire_time || user?.valid_until
  const remaining = getRemainingDays(expireAt)

  const loadPlans = useCallback(async () => {
    try {
      const res = await backend.getMembershipPlans()
      if (res.success && res.plans?.length) {
        setPlans(res.plans)
      } else {
        setPlans(DEFAULT_PLANS)
      }
    } catch (e) { setPlans(DEFAULT_PLANS) }
  }, [])

  const loadReferral = useCallback(async () => {
    setRefLoading(true)
    try {
      const res = await backend.getReferralInfo()
      const data = res?.data || res || {}
      setReferralInfo(data)
      setReferralCode(data.referral_code || data.code || user.referral_code || '')
      setRecords(data.referrals || data.records || [])
    } catch (e) { message.error('加载推荐信息失败') }
    finally { setRefLoading(false) }
  }, [user.referral_code])

  const loadRefConfig = useCallback(async () => {
    try {
      const res = await backend.adminGetReferralConfig()
      if (res.success) {
        setRefReward(res.referralReward || '新用户注册奖励')
      }
    } catch (e) {}
  }, [])

  useEffect(() => { loadPlans(); loadReferral(); loadRefConfig() }, [loadPlans, loadReferral, loadRefConfig])

  const copyCode = () => {
    if (!referralCode) return
    navigator.clipboard.writeText(referralCode).then(() => message.success('推荐码已复制')).catch(() => {
      const input = document.createElement('input')
      input.value = referralCode; document.body.appendChild(input)
      input.select(); document.execCommand('copy'); document.body.removeChild(input)
      message.success('推荐码已复制')
    })
  }

  const handleRenew = async () => {
    if (!renewalCode.trim()) { message.warning('请输入激活码'); return }
    setRenewing(true)
    try {
      const res = await backend.renew(renewalCode.trim())
      if (res?.success || res?.user) {
        message.success('续费成功')
        setRenewalCode('')
        if (res.user) sessionStorage.setItem('auth_user', JSON.stringify(res.user))
      } else {
        message.error(res?.error || '续费失败')
      }
    } catch (e) { message.error('续费失败') }
    finally { setRenewing(false) }
  }

  const handleChangePassword = async () => {
    if (!currentPassword.trim()) { message.warning('请输入当前密码'); return }
    if (!newPassword.trim() || newPassword.trim().length < 6) { message.warning('新密码至少6位'); return }
    if (newPassword.trim() !== confirmPassword.trim()) { message.warning('两次输入的新密码不一致'); return }
    if (currentPassword.trim() === newPassword.trim()) { message.warning('新密码不能与当前密码相同'); return }
    setChangingPassword(true)
    try {
      const res = await backend.changePassword(currentPassword.trim(), newPassword.trim())
      if (res.success) {
        message.success('密码修改成功')
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
      } else {
        message.error(res.error || '密码修改失败')
      }
    } catch (e) { message.error('密码修改失败') }
    finally { setChangingPassword(false) }
  }

  const refColumns = [
    { title: '被推荐人', dataIndex: 'username', key: 'username', render: (val, record) => record.referred_user || record.username || val || '-' },
    { title: '奖励', dataIndex: 'reward', key: 'reward', render: val => val ? <Tag color="gold">{val}</Tag> : '-' },
    { title: '时间', dataIndex: 'created_at', key: 'created_at', render: val => val ? new Date(val).toLocaleString('zh-CN') : '-' }
  ]

  const planColumns = [
    { title: '功能', dataIndex: 'label', key: 'label', width: 140 },
    ...plans.map(p => ({
      title: <Tag color={LEVEL_COLORS[p.level] || 'default'}>{p.name || p.level}</Tag>,
      dataIndex: p.level, key: p.level, align: 'center',
      render: val => val ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} /> : <CloseCircleOutlined style={{ color: '#d9d9d9', fontSize: 16 }} />
    }))
  ]

  const planDataSource = FEATURE_DEFS.map(fd => {
    const row = { key: fd.key, label: fd.label }
    plans.forEach(p => { row[p.level] = (p.features || []).includes(fd.key) })
    return row
  })

  const members = [
    { key: 'membership', label: <span><CrownOutlined /> 会员信息</span> },
    { key: 'renewal', label: <span><IdcardOutlined /> 账号续费</span> },
    { key: 'password', label: <span><LockOutlined /> 修改密码</span> },
    { key: 'referral', label: <span><TeamOutlined /> 我的推荐</span> },
    { key: 'plans', label: <span><CheckCircleOutlined /> 方案对比</span> }
  ]

  const renderMembershipInfo = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {isAdmin ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CrownOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
            <div style={{ marginTop: 16 }}>
              <Tag color="red" style={{ fontSize: 16, padding: '4px 16px' }}>管理员</Tag>
            </div>
            <div style={{ marginTop: 16 }}><Text style={{ fontSize: 18, color: '#666' }}>管理员账号，永久有效</Text></div>
          </div>
        </Card>
      ) : (
        <>
          <Card>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <CrownOutlined style={{ fontSize: 48, color: '#faad14' }} />
              <div style={{ marginTop: 16 }}>
                <Tag color={LEVEL_COLORS[level] || 'blue'} style={{ fontSize: 14, padding: '4px 20px' }}>
                  {LEVEL_NAMES[level] || level?.toUpperCase() || 'BASIC'}
                </Tag>
              </div>
            </div>
          </Card>

          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 24 }}>
              <Statistic title="到期时间" value={expireAt ? new Date(expireAt).toLocaleDateString('zh-CN') : '未知'} prefix={<CalendarOutlined />} />
              <Statistic title="剩余天数" value={remaining} suffix="天" prefix={<ClockCircleOutlined />}
                valueStyle={{ color: remaining <= 7 ? '#ff4d4f' : remaining <= 30 ? '#faad14' : '#52c41a' }} />
            </div>
          </Card>

          {plans.length > 0 && (
            <Card title="当前方案功能">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(plans.find(p => p.level === level)?.features || []).map(fk => {
                  const fd = FEATURE_DEFS.find(f => f.key === fk)
                  return fd ? <Tag key={fk} color="green">{fd.label}</Tag> : <Tag key={fk}>{fk}</Tag>
                })}
                {(!plans.find(p => p.level === level) || (plans.find(p => p.level === level)?.features || []).length === 0) && (
                  <Text type="secondary">暂无功能配置</Text>
                )}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  )

  const renderRenewal = () => (
    <Card title="账号续费">
      <Paragraph type="secondary">输入激活码为账号续费，续费天数累加到当前有效期</Paragraph>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Input value={renewalCode} onChange={e => setRenewalCode(e.target.value)}
          placeholder="请输入激活码" prefix={<IdcardOutlined />} style={{ flex: 1 }}
          onPressEnter={handleRenew} />
        <Button type="primary" onClick={handleRenew} loading={renewing}>续费</Button>
      </div>
    </Card>
  )

  const renderChangePassword = () => (
    <Card title="修改密码">
      <Paragraph type="secondary">修改登录密码，密码用于登录软件时使用</Paragraph>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 400 }}>
        <Input.Password value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
          placeholder="当前密码" prefix={<LockOutlined />}
          onPressEnter={handleChangePassword} />
        <Input.Password value={newPassword} onChange={e => setNewPassword(e.target.value)}
          placeholder="新密码（至少6位）" prefix={<LockOutlined />}
          onPressEnter={handleChangePassword} />
        <Input.Password value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
          placeholder="确认新密码" prefix={<LockOutlined />}
          onPressEnter={handleChangePassword} />
        <Button type="primary" onClick={handleChangePassword} loading={changingPassword}>修改密码</Button>
      </div>
    </Card>
  )

  const renderReferral = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card title="我的推荐码">
        {referralCode ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <Text strong style={{ fontSize: 18, letterSpacing: 2 }}>{referralCode}</Text>
              <Button type="primary" icon={<CopyOutlined />} onClick={copyCode} size="small">复制</Button>
            </div>
            {refReward && (
              <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6, padding: '8px 12px' }}>
                <Text style={{ fontSize: 13, color: '#d46b08' }}>
                  推荐奖励：{refReward} — 将推荐码分享给新用户，他们在激活时填写即可获得对应奖励
                </Text>
              </div>
            )}
          </>
        ) : (
          <Paragraph type="secondary">暂无推荐码</Paragraph>
        )}
      </Card>

      <Card title="推荐记录">
        <Table dataSource={records} columns={refColumns} rowKey={(_, index) => index}
          loading={refLoading} pagination={false} size="small" locale={{ emptyText: '暂无推荐记录' }} />
      </Card>
    </div>
  )

  const renderPlanComparison = () => (
    <Card title="会员方案对比">
      {plans.length > 0 ? (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            {plans.map(p => (
              <Card key={p.level} size="small" style={{ flex: 1, minWidth: 200, textAlign: 'center' }}
                bodyStyle={{ padding: 16 }}>
                <Tag color={LEVEL_COLORS[p.level] || 'default'} style={{ fontSize: 14, padding: '2px 16px', marginBottom: 8 }}>
                  {p.name || p.level}
                </Tag>
                <div style={{ fontSize: 24, fontWeight: 'bold', margin: '8px 0' }}>{p.price || '免费'}</div>
              </Card>
            ))}
          </div>

          <Table dataSource={planDataSource} columns={planColumns} pagination={false} size="small"
            rowKey="key" bordered style={{ maxWidth: 700 }} />
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Text type="secondary">暂无方案数据，请联系管理员配置</Text>
        </div>
      )}
    </Card>
  )

  const tabContent = {
    membership: renderMembershipInfo(),
    renewal: renderRenewal(),
    password: renderChangePassword(),
    referral: renderReferral(),
    plans: renderPlanComparison()
  }

  return (
    <div>
      <Title level={4}>账号中心</Title>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={members.map(m => ({ ...m, children: tabContent[m.key] }))} />
    </div>
  )
}
